/**
 * English — the fallback language. This file defines the canonical key set;
 * every other language file must implement exactly these keys (TypeScript
 * enforces it via `Record<MessageKey, string>`), so adding a new UI string
 * here is a compile error in `de.ts`/`fr.ts`/`es.ts`/`cs.ts` until it's
 * translated there too.
 *
 * Interpolation: `{name}` placeholders are substituted by `t(key, params)`.
 * Keep parameter names identical across every language file.
 */
const en = {
  // ---------------------------------------------------------------- common
  'common.quoted': '"{value}"',
  'common.uciIdMissing': 'no UCI ID',
  'common.unknownPlaceholder': '?',
  'common.sexWoman': 'female',
  'common.sexMan': 'male',
  'common.optional': 'optional',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.done': 'Done',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.reset': 'Reset',
  'common.language': 'Language',
  'common.theme': 'Theme',
  'common.theme.system': 'System',
  'common.theme.light': 'Light',
  'common.theme.dark': 'Dark',
  'common.back': 'Back',

  // starter type labels (UI display only — the XML always uses the German
  // prefix from STARTER_TYPES.prefix, unrelated to UI language)
  'starterType.single': 'Single',
  'starterType.pair': 'Pair',
  'starterType.team4': 'Team of 4',
  'starterType.team6': 'Team of 6',

  // Programme.ageTier labels — UCI 8.2.002 (Elite/Junior) vs. 8.2.003 (Youth)
  'ageTier.eliteJunior': 'Elite / Junior',
  'ageTier.youth': 'Youth (Schüler)',

  // ------------------------------------------------------------------ app
  'app.title': 'Kür',
  'app.subtitle': 'Artistic Cycling Registration',
  'app.nav': 'Main navigation',
  'app.menu': 'Menu',
  'app.drawerMenu': 'Navigation menu',
  'app.open': 'Open',
  'app.save': 'Save',
  'app.importSelection': 'Import…',
  'app.exportSelection': 'Export…',
  'app.weboImport': 'WeBo import…',
  'app.settings': 'Settings',
  'app.about': 'About',
  'app.mainMissing': 'Main content area missing.',
  'app.opened': '{file} opened.',
  'app.openFailed': "This file can't be read as a workspace.",
  'nav.home': 'Home',
  'nav.registrationData': 'Registration',
  'nav.competitions': 'Competitions',
  'nav.clubs': 'Clubs',
  'nav.athletes': 'Athletes',
  'nav.starter': 'Starters',
  'nav.programme': 'Programme',
  'nav.entries': 'Entries',
  'nav.impressum': 'Legal notice',
  'nav.datenschutz': 'Privacy policy',

  // ----------------------------------------------------------------- home
  'home.welcome.title': 'Welcome to Kür',
  'home.welcome.text':
    'Kür helps you register athletes and their competition programmes for artistic cycling — from building a Kür to the finished XML export.',
  'home.howto.title': 'How it works',
  'home.howto.step1': 'Set up your [club:clubs] and [athletes:athletes]',
  'home.howto.step2': 'Assemble a Kür from the [figure catalogue:programme]',
  'home.howto.step3': 'Register for a [competition:competitions] and [export:entries] the XML',
  'home.start': 'Get started',
  'home.importExport.title': 'Import & export',
  'home.importExport.text':
    'The top bar (or the "⋮" menu on mobile) lets you [open:open] or [save:save] the whole workspace as a file, or [import:import] and [export:export] just part of it — like only the athletes or a single programme. The same buttons are also at the bottom of the [Entries:entries] screen.',
  'home.privacy.title': 'Your data stays on your device',
  'home.privacy.text':
    "Kür works entirely offline — everything you enter is stored only in your browser's local storage on this device. Nobody else, including us, can see it. That also means it's on you to save your workspace as a file and keep track of it — nothing is backed up anywhere else.",
  'home.install.title': 'Install Kür',
  'home.install.already': "You're using the installed app — it works offline and opens its own window.",
  'home.install.button': 'Install',
  'home.install.prompt': 'Install Kür as an app for one-tap access and full offline use, no browser bar needed.',
  'home.install.ios': 'Tap the Share icon, then "Add to Home Screen" — Kür then opens like any other app and works fully offline.',
  'home.install.macSafari':
    'Open the Share menu (or File → Add to Dock) and choose "Add to Dock" — Kür then opens like any other app and works fully offline.',
  'home.install.android':
    'Open the browser menu and look for "Add to Home screen" or "Install app" — Kür then opens like any other app and works fully offline.',
  'home.install.desktop':
    'Look for an install icon in the address bar, or check the browser menu for "Install Kür…" — it then opens in its own window and works fully offline.',
  'home.install.unsupported':
    "Your browser doesn't offer to install Kür as an app, but it still works fully offline once you've opened it here.",

  // ---------------------------------------------------------------- about
  'about.title': 'About Kür',
  'about.story.title': 'Why Kür exists',
  'about.story.p1': "My girlfriend grew up in artistic cycling and loves the sport with her whole heart. A while ago she and her team partner decided to give competing a second run. Again and again the two of them would sit together for an hour at a time just to put a programme together — and for a long time I didn't understand why it took so long.",
  'about.story.p2': "Then I looked at the tools still used for it today — solidly built for their time, but no longer state of the art, and every change took longer than it should have. If two seasoned athletes found that a challenge, it probably wasn't any easier for someone just starting out. As a former software development lead, I thought: this can be simpler today.",
  'about.story.p3': "So, with Claude's help, I built a mobile-first web app that makes putting a programme together, testing it and throwing it away again fast — with built-in checks and a visualisation of how the figures flow, so the shape of a programme is clear at a glance. That's how Kür came to be.",
  'about.howto.title': 'Building a registration',
  'about.howto.lede': 'The screens in the navigation follow this order, but you can jump between them any time — nothing has to be finished before you move on.',
  'about.howto.registrationData': 'Registration data — your club and the contact person responsible for the entry.',
  'about.howto.competitions': 'Competitions — every competition you want to register for.',
  'about.howto.clubs': 'Clubs — every participating club, created once and then simply picked wherever needed.',
  'about.howto.athletes': 'Athletes — every rider, each assigned to a club and later to starters.',
  'about.howto.starter': 'Starters — who competes together: a single, a pair, or a team of 4 or 6.',
  'about.howto.programme': 'Programme — the ordered sequence of figures a starter rides, built from the exercise catalogue.',
  'about.howto.entries': 'Entries — tie one starter, one programme and one competition together; each entry produces its own export.',
  'about.howto.footnote': 'Open an entry on the Entries screen to check it against both schemas and download its XML.',
  'about.sbom.title': 'Open-source components',
  'about.sbom.lede': "Kür is built on the open-source libraries below, generated automatically from the project's package.json — this list is always current.",
  'about.sbom.runtime': 'Shipped to your browser',
  'about.sbom.build': 'Build tooling only',
  'about.support.title': 'Support Kür',
  'about.support.text': "I don't make any money from Kür — I just want the artistic-cycling community to be free of the old tools. Hosting it and publishing it in the app stores still costs me around €200 a year, though. If Kür helps you, I'd be glad about a coffee every now and then. Some app versions show a small, unobtrusive ad banner to help with that, removable with a small in-app purchase — both stay entirely optional and never affect how Kür itself works.",

  // ------------------------------------------------------------------ legal
  'legal.impressum.placeholder':
    "Information pursuant to §5 of the German Digital Services Act (Digitale-Dienste-Gesetz, DDG)\n\nBenedikt-Alexander Mokroß\nc/o Impressumservice Dein-Impressum\nStettiner Str. 41\n35410 Hungen\nGermany\n\nContact\nEmail: info@kuer.eu\n\nKür is a private, non-commercial project.\n\nLiability for content\nAs a service provider, I am responsible for my own content on these pages under the general laws (§7(1) DDG). Under §§8–10 DDG, however, I am not obliged to monitor transmitted or stored third-party information, or to investigate circumstances that indicate unlawful activity. Obligations to remove or block the use of information under general law remain unaffected; liability is only possible from the point in time at which a specific infringement becomes known. Upon becoming aware of any such infringement, I will remove the content in question without delay.\n\nLiability for links\nThis app may link to external third-party websites over whose content I have no control. I therefore cannot accept any liability for that third-party content; responsibility lies with the respective provider of the linked page. Linked pages were checked for recognisable legal violations at the time of linking. Permanent monitoring of linked pages without concrete evidence of an infringement is not reasonable. Upon becoming aware of any legal violation, I will remove such links without delay.\n\nCopyright\nContent I created on these pages is subject to German copyright law. Third-party contributions are marked as such. Reproduction, editing, distribution or any form of use beyond the scope of copyright law requires my written consent.",
  'legal.datenschutz.placeholder':
    "Privacy policy pursuant to the GDPR\n\nController\nBenedikt-Alexander Mokroß\nc/o Impressumservice Dein-Impressum\nStettiner Str. 41\n35410 Hungen\nGermany\nEmail: info@kuer.eu\n\nOverview\nKür processes practically no personal data on a server. Your workspace — clubs, athletes, starters, programmes, entries — is stored exclusively on your device, in your browser, and is never transmitted automatically. There is no user account, no sign-in and no server-side storage of your workspace data.\n\nLocal storage in your browser\nThe app stores your current workspace, your language and theme preference, and your cookie choice in your device's local storage. This data never leaves your device unless you actively export it as a file or generate an XML entry to download. The legal basis is Art. 6(1)(b) GDPR for your workspace (fulfilling the purpose you use the app for) and Art. 6(1)(f) GDPR for convenience settings like language and theme (a legitimate interest in an app that works as expected). This data remains stored until you delete it or clear your browser storage — there is no automatic deletion period, and no server-side retention because there is no server-side storage.\n\nCookie settings\nOn your first visit, the app asks whether additional features that are not yet built — usage statistics and crash/error reports — may become active in the future. Your choice is stored locally and indefinitely (see above) and can be changed at any time via \"Cookie settings\" in the menu. As long as these features don't exist, your choice has no effect: no corresponding data is collected or transmitted.\n\nFonts\nThe fonts used, Archivo and IBM Plex Mono, ship with the app and are served from our own server. No connection is made to Google Fonts or any other font provider.\n\nHosting and server log files\nThis website is hosted by STRATO GmbH. When you access the site, the host automatically processes technical access data — including IP address, date and time of access, the file requested, the amount of data transferred, browser/operating system, and the referrer URL — as is technically necessary for operating any web server. These log files serve the technical delivery, stability and security of the website and are not combined with any other data source. The legal basis is Art. 6(1)(f) GDPR (a legitimate interest in a functioning, secure service). Where legally required, a data processing agreement under Art. 28 GDPR is in place with STRATO.\n\nContacting us by email\nIf you email me, I process the data you provide — in particular your email address and the content of your message — solely to handle your enquiry (Art. 6(1)(b) or (f) GDPR). I delete this data once it is no longer needed, unless a statutory retention obligation applies.\n\nDisclosure to third parties\nYour data is not disclosed to any third party, other than the technically necessary processing by the hosting provider described under \"Hosting and server log files\".\n\nNo automated decision-making\nNo automated decision-making or profiling within the meaning of Art. 22 GDPR takes place.\n\nYour rights\nYou have the right to access (Art. 15 GDPR), rectification (Art. 16 GDPR), erasure (Art. 17 GDPR), restriction of processing (Art. 18 GDPR), data portability (Art. 20 GDPR) and objection to processing (Art. 21 GDPR). You may withdraw any consent at any time with effect for the future (Art. 7(3) GDPR), for example via the cookie settings. You also have the right to lodge a complaint with a supervisory authority (Art. 77 GDPR) — the authority ordinarily responsible is the Hessian Commissioner for Data Protection and Freedom of Information (HBDI), as I am based in Hesse, Germany.\n\nChanges to this policy\nThis privacy policy will be updated as soon as the actual data processing changes — for example if usage statistics, crash reports or server-side workspace storage are introduced. The version currently shown in the app is the one that applies.",

  // ---------------------------------------------------------------- consent
  'consent.title': 'Cookie settings',
  'consent.intro': 'Kür stores your workspace on this device only. If we later add features that send data elsewhere, they stay off until you allow them here — and you can change your choice at any time from this same screen.',
  'consent.category.necessary.label': 'Necessary',
  'consent.category.necessary.desc': 'Keeps the app working: saving your workspace on this device, and remembering your language and theme. Always on.',
  'consent.category.statistics.label': 'Usage statistics',
  'consent.category.statistics.desc': 'Anonymous usage data to see which features get used. Not built yet — this toggle has no effect until it is.',
  'consent.category.crashReports.label': 'Crash & error reports',
  'consent.category.crashReports.desc': 'Sends details about crashes so they can be fixed. Not built yet — this toggle has no effect until it is.',
  'consent.rejectAll': 'Necessary only',
  'consent.saveSelection': 'Save selection',
  'consent.acceptAll': 'Accept all',
  'consent.privacyLink': 'Read the full privacy policy',

  // ---- RegistrationDataView
  'registrationData.title': 'Registration data',
  'registrationData.lede': 'This information appears in the header of both XML files. Enter it once, use it for every entry.',
  'registrationData.club.title': 'Submitting club',
  'meldedaten.field.club': 'Club',
  'meldedaten.field.regionalFederation': 'Regional federation',
  'meldedaten.field.federation': 'Federation / district',
  'registrationData.contact.title': 'Contact / club official',

  // shared person fields (used by meldedaten contact, verein contact, athlete)
  'field.firstName': 'First name',
  'field.lastName': 'Last name',
  'field.uciId': 'UCI ID',
  'field.email': 'Email',
  'field.street': 'Street',
  'field.phone': 'Phone',
  'field.postalCode': 'Postal code',
  'field.city': 'City',
  'field.countryCode': 'Country code',
  'field.fax': 'Fax',
  'field.maxLength': 'max. {n}',

  // ---- CompetitionsView
  'competitions.title': 'Competitions',
  'competitions.lede': 'Add every competition you are registering for. Pick a competition, a starter and a programme for each entry on the Entries screen.',
  'competitions.field.name': 'Competition',
  'competitions.field.date': 'Date',
  'competitions.field.austragungsort': 'Venue',
  'competitions.field.organiser': 'Organiser',
  'competitions.add': 'Add competition',
  'competitions.empty.title': 'No competitions yet',
  'competitions.empty.text': 'Add a competition, then register entries for it on the Entries screen.',
  'competitions.unnamed': 'Unnamed competition',
  'competitions.form.title': 'Edit competition',
  'competitions.deleteConfirm': '{used} registration(s) are entered in this competition and will be removed too. Delete it anyway?',
  'competitions.deleted': 'Competition deleted.',

  // ---- ClubsView
  'clubs.title': 'Clubs',
  'clubs.lede': 'Each club is created once, then simply picked for every athlete afterwards.',
  'clubs.empty.title': 'No clubs yet',
  'clubs.empty.text': "Create the participating clubs once — after that, a club is just picked for each athlete, not typed again.",
  'clubs.add': 'Add club',
  'clubs.unnamed': 'Unnamed',
  'clubs.row.number': 'No. {number}',
  'clubs.row.contact': 'Contact: {name}',
  'clubs.deleteConfirm': '{used} athlete(s) already have this club entered. It stays there as text. Delete anyway?',
  'clubs.deleted': 'Club deleted.',
  'clubs.form.title': 'Edit club',
  'clubs.field.name': 'Club',
  'clubs.field.nation': 'Nation',
  'clubs.field.federation': 'Federation',
  'clubs.field.number': 'Number',
  'clubs.contact.title': 'Contact person',

  // ---- AthletesView
  'athletes.title': 'Athletes',
  'athletes.lede': 'Each athlete is created once and can then be assigned to any number of starters.',
  'athletes.empty.title': 'No athletes yet',
  'athletes.empty.text': 'First create the athletes. Afterwards you put together starters from them.',
  'athletes.add': 'Add athlete',
  'athletes.unnamed': 'Unnamed athlete',
  'athletes.deleteConfirm': 'This athlete is assigned to {count} starter(s). Delete anyway?',
  'athletes.deleted': 'Athlete deleted.',
  'athletes.form.title': 'Edit athlete',
  'athletes.field.birthDate': 'Date of birth',
  'athletes.field.nation': 'Nation',
  'athletes.field.sex': 'Sex',
  'athletes.field.club': 'Club',
  'athletes.club.none': '– no club –',
  'athletes.club.emptyHint': 'no clubs created yet',

  // ---- StarterView
  'starter.title': 'Starters',
  'starter.lede': 'A starter is a single, a pair, or a team of 4 or 6 — with a discipline and its assigned athletes. Programmes and competition entries are put together separately.',
  'starter.empty.title': 'No starters yet',
  'starter.empty.text': 'A starter ties a discipline to the athletes competing together.',
  'starter.add': 'Add starter',
  'starter.unnamed': 'New starter',
  'starter.riderLimitToast': '{label} starts with {max} athletes.',
  'starter.deleteConfirm': 'This starter is used in {used} registration(s), which will be removed too. Delete anyway?',
  'starter.deleted': 'Starter deleted.',
  'starter.form.title': 'Edit starter',
  'starter.field.type': 'Type',
  'starter.field.discipline': 'Discipline',
  'starter.field.startername': 'Starter name',
  'starter.field.startername.hint': 'empty = from the surnames',
  'starter.personen.title': 'Athletes',
  'starter.personen.needSportler': 'First create people under <b>Athletes</b>.',
  'starter.personen.pick': 'Athlete',
  'starter.personen.pickPlaceholder': '– choose athlete –',
  'starter.riderTag': '{have} of {need} athletes',
  'starter.sgTag': 'Joint entry (SG)',
  'starter.sexMismatchTag': 'Sex does not match the discipline',
  'starter.derived.club': 'Club: <b>{club}</b>',
  'starter.derived.sg': ' · <b>Joint entry (SG)</b>',
  'starter.derived.sex': ' · Scored as: <b>{sex}</b>',
  'starter.derived.note': "Derived from the athletes' clubs and sex.",
  'starter.derived.note.alias': 'Shortened from the full club name "{full}" for the export.',
  'starter.derived.note.autoTruncated': 'Automatically shortened to {max} characters for the export, like WeBo2008 does. Give it a custom short name below to use a nicer abbreviation instead.',
  'starter.aliasTag': 'Shortened club name',
  'starter.clubAlias.autoTruncated': 'The combined club name "{full}" is {len} characters — the export only allows {max}, so it was cut to fit. Give it a shorter name below for a nicer abbreviation.',
  'starter.clubAlias.aliasStillOverLimit': 'The short club name is still {len} characters — the export only allows {max}. Shorten it further.',
  'starter.field.clubAlias': 'Short club name',
  'starter.field.clubAlias.hint': 'used in the export instead of the full name above',
  'starter.sexMismatch.one': 'This discipline is a women\'s category, but {names} is not recorded as female.',
  'starter.sexMismatch.many': "This discipline is a women's category, but {names} are not recorded as female.",
  'starter.personalBest.title': 'Personal best',
  'starter.field.points': 'Points',
  'starter.field.date': 'Date',
  'starter.field.venue': 'Venue',
  'starter.field.scoringReportEmail': 'Email for the scoring report',

  // ---- ProgrammeView
  'programme.title': 'Programmes',
  'programme.titleWithName': 'Programme: {name}',
  'programme.editName': 'Rename programme',
  'programme.select': 'Choose programme',
  'programme.list.meta': '{count}/{limit} · {points} pts',
  'programme.empty.title': 'No programmes yet',
  'programme.empty.text': 'A programme belongs to a type of starter (single, pair, team of 4 or 6) and an age tier, not to one specific starter — it gets assigned to a starter and a competition on the Entries screen.',
  'programme.new.button': 'New programme',
  'programme.new.title': 'New programme',
  'programme.new.type': 'Type',
  'programme.new.tier': 'Age tier',
  'programme.new.name': 'Name',
  'programme.new.namePlaceholder': 'e.g. "Kür A" — only needed to tell several apart',
  'programme.new.create': 'Create',
  'programme.deleteProgramme': 'Delete programme',
  'programme.deleteConfirm': 'This programme is used in {used} registration(s), which will be removed too. Delete anyway?',
  'programme.deleted': 'Programme deleted.',
  'programme.duplicateProgramme': 'Duplicate programme',
  'programme.duplicated': 'Programme duplicated.',
  'programme.duplicateName': '{name} (Copy)',
  'programme.readout.aufgestellt': 'Declared total',
  'programme.readout.uebungen': 'Exercises',
  'programme.readout.profil': 'Difficulty profile',
  'programme.profileEmpty': 'still empty',
  'programme.duplicate': 'Entered twice: <b>{list}</b>. An exercise only counts once — the repeat scores no points.',
  'programme.bikes.two': 'On two bicycles',
  'programme.bikes.one': 'On one bicycle',
  'programme.exercises.empty.title': 'Programme is empty',
  'programme.exercises.empty.text': 'Choose exercises from the catalogue for {label}.',
  'programme.addExercise': 'Add exercise',
  'programme.clear': 'Clear programme',
  'programme.clearConfirm': 'Remove all exercises from this programme?',
  'programme.cleared': 'Programme cleared.',
  'programme.share': 'Share programme',
  'programme.catalogueLoading': 'Loading the figure catalogue…',
  'programme.figureNotFound': 'Figure not found',
  'programme.tactical.title': 'Tactical enlargement',
  'programme.tactical.upTo': 'T up to {points}',
  'programme.final.title': 'UCI 8.2.011: only allowed as the last exercise of the programme, or immediately before the bicycle change',
  'programme.final.tag': 'Final figure',
  'programme.dragHandle': 'Drag to reorder',
  'programme.moveUp': 'Move up',
  'programme.moveDown': 'Move down',
  'programme.removeExercise': 'Remove exercise',

  // ---- EntriesView
  'entries.title': 'Registration entries',
  'entries.lede': 'One entry ties one starter to one competition and one matching programme. Each entry exports the Kunstradmeldung/Teammeldung for its competition.',
  'entries.empty.title': 'No entries yet',
  'entries.empty.text': 'Add an entry, then pick its competition, starter and programme.',
  'entries.add': 'Add entry',
  'entries.deleted': 'Entry deleted.',
  'entries.field.comp': 'Competition',
  'entries.field.starter': 'Starter',
  'entries.field.programm': 'Programme',
  'entries.pickPlaceholder': '– choose –',
  'entries.pickStarterFirst': 'choose a starter first',
  'entries.incomplete': 'Incomplete',
  'entries.export': 'Export',
  'entries.unset': 'not set',

  // -------------------------------------------------- programme share link
  'share.imported': 'Imported "{name}" from a shared link.',
  'share.importedWithSkipped':
    'Imported "{name}" — {count} figure(s) could not be found and were skipped.',
  'share.decodeError': 'This link could not be read — it may be corrupted or from a newer version of the app.',

  // --------------------------------------------------------- export-sheet
  'export.sheet.title': 'Export — {comp}',
  'export.check.title': 'Check',
  'export.check.ok': 'Every required field in both schemas is complete.',
  'export.loading': 'Loading the figure catalogue…',
  'export.issue.view': 'view',
  'export.kunstrad.title': 'Kunstradmeldung',
  'export.kunstrad.note': 'Starters, athletes and programme — kunstradmeldung.xsd, version 19.0. {starters} starters, {exercises} exercises.',
  'export.downloadXml': 'Download XML',
  'export.copyClipboard': 'Copy to clipboard',
  'export.team.title': 'Teammeldung',
  'export.team.note': 'Annual team registration — teammeldung.xsd, version 1.1. {count} team{plural}.',
  'export.team.empty': 'As soon as a pair, team-of-4 or team-of-6 entry exists, the Teammeldung appears here.',
  'export.workspace.title': 'Workspace',
  'export.workspace.note': 'Saves club, athlete and programme data as a file you can reopen later.',
  'export.workspace.noStorage': ' This browser does not save anything automatically — save regularly.',
  'export.workspace.save': 'Save workspace',
  'export.workspace.open': 'Open workspace',
  'export.workspace.import': 'Import selection…',
  'export.workspace.exportSelection': 'Export selection…',
  'export.workspace.reset': 'Reset everything',
  'export.resetConfirm': 'Delete all data in this browser? Saved workspace files are unaffected.',
  'export.resetDone': 'Reset.',

  // ------------------------------------------------------------ figure-picker
  'picker.title': 'Add exercise',
  'picker.searchPlaceholder': 'Search number, name or term …',
  'picker.searchLabel': 'Search exercise',
  'picker.groupLabel': 'Exercise group',
  'picker.allGroups': 'All groups',
  'picker.minPlaceholder': 'from',
  'picker.maxPlaceholder': 'to',
  'picker.minLabel': 'Points from',
  'picker.maxLabel': 'Points to',
  'picker.reset': 'Reset',
  'picker.done': 'Done',
  'picker.loading': 'Loading exercise catalogue …',
  'picker.loadFailed': "The exercise catalogue couldn't be loaded.",
  'picker.tooMany': 'More than {limit} exercises are not allowed.',
  'picker.added': '{id} added · {points} pts',
  'picker.tacticalTitle': 'Tactical enlargement possible',
  'picker.finalTitle': 'UCI 8.2.011: only allowed as a final figure',
  'picker.noResults': "No exercise matches this search. Try other terms or filters.",
  'picker.limitReached': 'Maximum of {limit} exercises reached.',
  'picker.info': '{shown} results · {have} of {limit} assembled · {points} pts',

  // --------------------------------------------------------------- rules.ts
  'rules.count.exceeded': '{count} exercises assembled, at most {max} are allowed (UCI {article}).',
  'rules.onBike.tooFew': '{count} exercises on one bicycle, at least {min} are required (UCI {article}).',
  'rules.onBike.tooMany': '{count} exercises on one bicycle, at most {max} are allowed (UCI {article}).',
  'rules.onBike.needTwo': 'The programme also needs exercises on two bicycles — both are required (UCI {article}).',
  'rules.final.single': 'Exercise {nr} (#{pos}) is a final figure and may only stand as the last exercise of the programme (UCI 8.2.011).',
  'rules.final.pair': 'Exercise {nr} (#{pos}) is a final figure and may only stand as the last exercise of the programme or immediately before the bicycle change (UCI 8.2.011).',
  'rules.raiser.continuity': 'Exercise {nr} (#{pos}) ends in the {label} — the following exercise must continue in the same kind of raiser (UCI 8.2.028).',
  'rules.bikes.multipleSwitches': 'Exercise {nr} (#{pos}) switches back between one and two bicycles — a pair programme may change from one to the other only once (UCI 8.2.006).',
  'rules.figure.repeated': 'Exercise {nr} (#{pos}) repeats figure {first} (#{firstPos}) — every figure may appear only once in the programme (UCI 8.1.019).',

  // -------------------------------------------------------------- validate.ts
  'validate.meta.clubMissing': 'Registration data: the submitting club is missing.',
  'validate.meta.clubTooLong': 'Registration data: club name is too long',
  'validate.meta.federationTooLong': 'Registration data: federation is too long',
  'validate.contact.firstNameMissing': 'Contact: first name is missing.',
  'validate.contact.lastNameMissing': 'Contact: last name is missing.',
  'validate.contact.emailMissing': 'Contact: email is missing.',
  'validate.contact.emailTooLong': 'Contact: email is too long',
  'validate.contact.uciIdTooLong': 'Contact: UCI ID is too long',
  'validate.comp.nameMissing': 'Competition: name is missing.',
  'validate.comp.dateMissing': 'Competition: date is missing.',
  'validate.comp.venueMissing': 'Competition: venue is missing.',
  'validate.comp.dateInvalid': 'Competition: date is not a valid date.',
  'validate.comp.noneDefined': 'No competition has been created yet.',
  'validate.comp.noneSelected': 'No competition is selected for export.',
  'validate.noStarters': 'No starter is entered in this competition.',
  'validate.entry.incomplete': '{count} registration(s) are still missing a starter or a programme and will not be exported.',
  'validate.entry.duplicateStarter': '{who} is entered {count} times for this competition — each starter may appear only once.',
  'validate.entry.typeMismatch': '{who} is a {starterType}, but its chosen programme is for a {programType} — pick a matching programme.',
  'validate.starter.riderCount': '{who}: {count} athletes assigned, {label} needs {needed}.',
  'validate.starter.sexMismatch.one': '{who}: discipline is a women\'s category, but {names} is not recorded as female.',
  'validate.starter.sexMismatch.many': "{who}: discipline is a women's category, but {names} are not recorded as female.",
  'validate.starter.nameTooLong': '{who}: starter name is too long',
  'validate.starter.clubTooLong': "{who}: club name (from the athletes' clubs) is too long",
  'validate.starter.disciplineTooLong': '{who}: discipline is too long',
  'validate.starter.personalBestTooLong': '{who}: personal best is too long',
  'validate.starter.scoringReportEmailTooLong': '{who}: scoring-report email is too long',
  'validate.starter.bestDateInvalid': '{who}: personal-best date is not a valid date.',
  'validate.programm.empty': '{who}: the programme is empty.',
  'validate.programm.tooMany': '{who}: {count} exercises assembled, at most {max} are allowed.',
  'validate.uebung.notFound': '{who}: exercise {nr} is not in the figure catalogue.',
  'validate.uebung.duplicate': '{who}: exercise {nr} is entered more than once and counts only once.',
  'validate.athlete.missing': '{who}: an assigned athlete no longer exists.',
  'validate.athlete.nameMissing': '{who}: {name} — first or last name is missing.',
  'validate.athlete.uciIdMissing': '{who}: {name} — UCI ID is missing.',
  'validate.athlete.uciIdTooLong': '{who}: {name} — UCI ID is too long',
  'validate.athlete.birthDateInvalid': '{who}: {name} — date of birth is missing or invalid.',
  'validate.athlete.nationInvalid': '{who}: {name} — nation "{nation}" is not allowed by the schema.',
  'validate.singlesWarning': '{count} single starter(s) only appear in the Kunstradmeldung — the Teammeldung has no single disciplines.',
  'validate.noTeams': 'No team has been created — only a Kunstradmeldung will result.',
  'validate.maxChars': ' (max. {n} characters).',

  // ------------------------------------------------------------------ lib
  'lib.downloaded': '{filename} downloaded.',
  'lib.downloadFailedCopied': "Download not possible — content copied to the clipboard.",
  'lib.copiedClipboard': 'Copied to the clipboard.',
  'lib.copyFailed': 'Copying not possible — select the text in the preview field.',

  // ---------------------------------------------------------- import/export
  'ie.title.import': 'Import selection',
  'ie.title.export': 'Export selection',
  'ie.import.pick': 'Choose file to import…',
  'ie.import.invalid': "This file can't be read as a workspace.",
  'ie.import.noneSelected': 'Nothing is selected.',
  'ie.import.done': '{clubs} clubs, {athletes} athletes, {comps} competitions, {starters} starters, {programs} programmes, {entries} entries imported ({skipped} already present, skipped).',
  'ie.export.done': 'Exported.',
  'ie.section.clubs': 'Clubs',
  'ie.section.athletes': 'Athletes',
  'ie.section.comps': 'Competitions',
  'ie.section.starters': 'Starters',
  'ie.section.programs': 'Programmes',
  'ie.section.entries': 'Entries',
  'ie.section.empty': 'none',
  'ie.item.starter': '{name} ({type})',
  'ie.item.program': '{name} ({count} exercises)',
  'ie.item.entry': '{starter} — {comp}',
  'ie.expand': 'Expand',
  'ie.collapse': 'Collapse',
  'ie.selectAll': 'Select all',
  'ie.selectNone': 'Select none',
  'ie.confirm.import': 'Import',
  'ie.confirm.export': 'Export',

  'webo.title': 'WeBo2008 import',
  'webo.import.pick': 'Choose a WeBo XML file…',
  'webo.import.invalid': "This file can't be read as a WeBo2008 export (neither a Kunstradmeldung nor a Teammeldung XML).",
  'webo.import.noneSelected': 'Nothing is selected.',
  'webo.import.done':
    '{comps} competition, {starters} starters ({athletesNew} new athletes, {athletesReused} already known — {athletesFilled} of those had missing details filled in), {clubsNew} new clubs, {programs} programmes, {entries} entries imported.',
  'webo.category.registrationData': 'Registration header',
  'webo.category.registrationData.hint': 'Contact person, club and Landesverband — overwrites the current registration data',
  'webo.category.wettbewerb': 'Competition',
  'webo.category.wettbewerb.unavailable': 'This file carries no competition data (Teammeldung has none).',
  'webo.category.starter': 'Starters',
  'webo.category.programme': 'Programmes',
  'webo.category.programme.unavailable': 'This file carries no programme data (Teammeldung has none).',
  'webo.item.entry': '{name} — {discipline}',
  'webo.item.programme.ok': '{count} figures, {points} pts.',
  'webo.item.programme.mismatch': '{count} figures ({unmatched} unknown), points differ ({rebuilt} instead of {declared})',
  'webo.warnings.title': '{count} thing(s) worth a look:',
  'webo.warning.unknownNation': '{name}: nation "{nation}" is not recognized.',
  'webo.warning.unknownRegionalFederation': 'Landesverband "{value}" is not recognized.',
  'webo.warning.unknownDiscipline': 'Discipline "{discipline}" not recognized — starter type guessed from {count} athlete(s).',
  // ------------------------------------------------------------------- pwa
  'pwa.offlineReady': 'Kür is ready to use offline.',
  'pwa.updateAvailable': 'A new version is ready.',
  'pwa.reload': 'Reload',
} satisfies Record<string, string>;

export default en;
export type MessageKey = keyof typeof en;
